<template>
  <div class="ask-ai-page">
    <!-- New fixed header (same as in AskAIPage) -->
    <header class="header">
      <div class="logo">
        <img src="@/assets/taxscanai-logo.png" alt="Taxscan Logo" />
      </div>
      <button class="logout-button" @click="logout">
        <span class="desktop-label">Signout</span>
        <span class="mobile-label">S</span>
      </button>
    </header>

    <!-- Main content container; note the absence of extra top margin -->
    <div class="content-container">
      <!-- Retaining balance text as before -->
      <div class="page-header">
        <div :class="['balance-text', balanceClass]">
          You have a balance of ₹{{ creditBalance }}
        </div>
      </div>

      <div class="question-card">
        <h1>Provide Additional Context <small>(Optional)</small></h1>

        <!-- Show original question for reference -->
        <p class="original-question" v-if="originalQuestion">
          <strong>Your Question:</strong> {{ originalQuestion }}
        </p>

        <label for="context">Additional Context:</label>
        <textarea
          ref="contextField"
          v-model="context"
          id="context"
          placeholder="Provide context if needed..."
          rows="12"
        ></textarea>
        <p class="instructions">
          This helps the AI give more accurate responses.
        </p>
        <p class="char-limit">{{ context.length }}/200</p>

        <div class="button-group">
          <button @click="goBack" class="secondary-button">Back</button>
          <button @click="submitForm" class="primary-button">Submit</button>
        </div>
      </div>
    </div>
  </div>
  <footer class="footer">
      <p>© {{ new Date().getFullYear() }} Taxscan Edutech Private Limited. All rights reserved.</p>
  </footer>
</template>

<script>
import axios from 'axios';

const AI_ENDPOINT =
  process.env.VUE_APP_AI_ENDPOINT || 'https://api.taxscan.in/api/summary';
const NODE_ENDPOINT =
  process.env.VUE_APP_NODE_ENDPOINT || 'http://localhost:3000/api/summary';

export default {
  name: 'ContextPage',
  data() {
    return {
      context: '',
      error: '',
      loading: false,
      response: null,
    };
  },
  computed: {
    originalQuestion() {
      return localStorage.getItem('user_question');
    },
    creditBalance() {
      const balance = localStorage.getItem('credit_balance') || '0';
      return parseFloat(balance);
    },
    balanceClass() {
      if (this.creditBalance < 50) {
        return 'red-balance';
      } else if (this.creditBalance <= 150) {
        return 'yellow-balance';
      } else {
        return 'green-balance';
      }
    },
  },
  methods: {
    goBack() {
      this.$router.push('/ask-ai');
    },
    async submitForm() {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          alert('You must be logged in!');
          await this.$router.push('/login');
          return;
        }

        const question = localStorage.getItem('user_question') || '';
        if (!question) {
          alert('Question not found. Please go back and submit your question.');
          await this.$router.push('/ask-ai');
          return;
        }

        const credit = parseFloat(localStorage.getItem('credit_balance') || '0');
        if (credit <= 0) {
          alert('Insufficient credit. Please top up to continue.');
          return;
        }

        await this.$router.push('/loading');

        const result = await axios.post(
          AI_ENDPOINT,
          { question, context: this.context },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const bodyForServer = {
          question,
          context: this.context,
          message: result.data.message,
          total_cost: result.data.total_cost,
          input_token_count: result.data.input_token_count,
          output_token_count: result.data.output_token_count,
        };

        const nodeResult = await axios.post(NODE_ENDPOINT, bodyForServer, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const prefix = 'Retrieved information:';
        let newMessage = result.data.message || '';
        if (newMessage.toLowerCase().startsWith(prefix.toLowerCase())) {
          newMessage = newMessage.substring(prefix.length).trim();
        }
        localStorage.setItem('ai_response', newMessage);


        if (nodeResult.data.updated_credit !== undefined) {
          localStorage.setItem('credit_balance', nodeResult.data.updated_credit);
        }
      } catch (error) {
        console.error('Error fetching response!', error);
        if (error.response && error.response.status === 401) {
          alert('Session expired or invalid. Please login again.');
          localStorage.removeItem('auth_token');
          await this.$router.replace('/login');
          return;
        }
        localStorage.setItem(
          'ai_response',
          '⚠️ Error retrieving response. Please try again.'
        );
        await this.$router.replace('/response');
      }
      await this.$router.replace('/response');
    },
    logout() {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('credit_balance');
      localStorage.removeItem('user_question');
      this.$router.push('/login');
    },
  },
  mounted() {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      this.error = 'Authentication failed. Please login again.';
      this.$router.push('/login');
      return;
    }
    this.$nextTick(() => {
      if (this.$refs.contextField) {
        this.$refs.contextField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.$refs.contextField.focus();
      }
    });
  },
};
</script>

<style scoped>
/* Base theme similar to AskAIPage */
.ask-ai-page {
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  /* Offset main content for the fixed header */
  padding-top: 10px;
  position: relative;
}

/* Fixed header styling */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.logo img {
  max-height: 140px;
  transition: max-height 0.3s;
}

/* Logout button styling (responsive) */
.logout-button {
  background: #007bff;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  white-space: nowrap;
  transition: background-color 0.3s;
}

.logout-button:hover {
  background: #0056b3;
}

.logout-button .mobile-label {
  display: none;
}

@media (max-width: 768px) {
  .logo img {
    max-height: 100px;
  }
  .logout-button .desktop-label {
    display: none;
  }
  .logout-button .mobile-label {
    display: inline;
  }
  .footer {
    font-size: 11px; /* or any size you prefer for mobile devices */
  }
}

/* Main content container */
.content-container {
  max-width: 800px;
  margin: 10px auto 20px auto;
  padding: 20px;
}

/* Page header for balance text */
.page-header {
  margin-bottom: 20px;
  text-align: right;
}

.balance-text {
  font-size: 16px;
  font-weight: 500;
}

/* Balance color classes */
.red-balance {
  color: red;
}
.yellow-balance {
  color: #d4c100;
}
.green-balance {
  color: green;
}

.question-card {
  background: #ffffff;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.question-card h1 {
  margin-bottom: 20px;
  font-size: 1.5rem;
}

.question-card label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
}

.question-card textarea {
  width: 100%;
  padding: 10px; /* Use 10px to match AskAIPage */
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: none;
  box-sizing: border-box;
  margin-bottom: 10px;
}

.instructions {
  font-size: 0.95rem;
  color: #555;
  margin-bottom: 10px;
}

.char-limit {
  font-size: 0.9rem;
  color: #999;
  text-align: right;
  margin-bottom: 20px;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.primary-button,
.secondary-button {
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: 0.3s;
}

.primary-button {
  background-color: #007bff;
  color: #fff;
}

.primary-button:hover {
  background-color: #0056b3;
}

.secondary-button {
  background-color: #6c757d;
  color: #fff;
}

.secondary-button:hover {
  background-color: #5a6268;
}

</style>

